import { Archive } from "lucide-react";
import { usePreferencesStore } from "@/lib/store/preferencesStore";
import { useArchivedCount } from "@/features/common/api/useArchivedCount";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

interface ShowArchivedToggleProps {
  doctype: string;
  /** When true, toggle drives ?archived=true URL param instead of preferences store. */
  useUrlParam?: boolean;
}

/**
 * Pill toggle that shows/hides archived (disabled=1) records in list pages.
 * Renders nothing if the doctype has no disabled field.
 *
 * Supports two modes:
 * - Store mode (default): toggles preferences store state
 * - URL mode (useUrlParam=true): toggles ?archived=true search param — bookmarkable
 */
export function ShowArchivedToggle({ doctype, useUrlParam }: ShowArchivedToggleProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const storeShowArchived = usePreferencesStore(
    (s) => s.showArchived[doctype] ?? false,
  );
  const setShowArchived = usePreferencesStore((s) => s.setShowArchived);
  const { data } = useArchivedCount(doctype);

  const count = data?.count ?? 0;
  const hasDisabledField = data?.has_disabled_field ?? false;

  // Determine active state based on mode
  const isActive = useUrlParam
    ? searchParams.get("archived") === "true"
    : storeShowArchived;

  const handleToggle = () => {
    if (useUrlParam) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (isActive) {
          next.delete("archived");
        } else {
          next.set("archived", "true");
        }
        return next;
      });
    } else {
      setShowArchived(doctype, !storeShowArchived);
    }
  };

  // Don't render for doctypes without a disabled field
  if (!hasDisabledField) return null;

  return (
    <button
      type="button"
      onClick={handleToggle}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs border transition-colors",
        isActive
          ? "bg-muted text-foreground border-border"
          : "bg-transparent text-muted-foreground border-border",
      )}
      aria-pressed={isActive}
    >
      <Archive className="size-3" />
      {isActive ? "Showing archived" : "Show archived"}
      {count > 0 && (
        <span className="text-muted-foreground">({count})</span>
      )}
    </button>
  );
}
